import { useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Checkbox, Stack, Text, TextLink } from 'components/ui';
import { AuthSocialButtons, AuthTextField } from 'pages/Auth/components';
import { useAuthInitialFocus } from 'pages/Auth/hooks';
import {
  SIGN_IN_FORGOT_PASSWORD_LABEL,
  SIGN_IN_IDENTIFIER_LABEL,
  SIGN_IN_IDENTIFIER_PLACEHOLDER,
  SIGN_IN_IDENTIFIER_SUBMIT_LABEL,
  SIGN_IN_REMEMBER_ME_LABEL,
} from 'pages/Auth/SignIn/SignIn.constants';
import { SIGN_IN_TEST_IDS } from 'pages/Auth/SignIn/SignIn.testIds';
import { isSignInIdentifierReady } from 'pages/Auth/SignIn/SignIn.utils';
import { getVisibleFieldErrorMessage, hasVisibleFieldError } from 'pages/Auth/utils';
import type { SignInIdentifierStepProps } from './SignInIdentifierStep.types';

const SignInIdentifierStep = ({
  form,
  isSubmitting,
  isSocialPending,
  onMicrosoftLogin,
  onRememberSignInChange,
  onStartGoogleLogin,
  onSubmit,
  rememberSignIn,
  resetPasswordHref,
  socialProviderPending,
}: SignInIdentifierStepProps) => {
  const isBusy = isSocialPending || isSubmitting;
  const focusIdentifierField = useCallback(() => {
    form.setFocus('identifier');
  }, [form]);

  useAuthInitialFocus(focusIdentifierField, !isBusy);
  const watchedIdentifier = form.watch('identifier');
  const hasBlockingIdentifierError = hasVisibleFieldError({
    error: form.formState.errors.identifier,
    hiddenErrorTypes: ['server'],
  });
  const isSubmitDisabled = isBusy || hasBlockingIdentifierError || !isSignInIdentifierReady(watchedIdentifier);

  return (
    <Stack component="form" noValidate onSubmit={onSubmit} spacing={2}>
      <AuthTextField
        autoComplete="username"
        disabled={isBusy}
        error={Boolean(form.formState.errors.identifier)}
        helperText={getVisibleFieldErrorMessage({
          error: form.formState.errors.identifier,
          hiddenErrorTypes: ['server'],
        })}
        id="auth-identifier-input"
        slotProps={{
          htmlInput: {
            autoCapitalize: 'none',
            autoCorrect: 'off',
            inputMode: 'email',
            spellCheck: false,
          },
        }}
        label={SIGN_IN_IDENTIFIER_LABEL}
        placeholder={SIGN_IN_IDENTIFIER_PLACEHOLDER}
        type="text"
        {...form.register('identifier')}
      />
      <Box alignItems="center" display="flex" flexWrap="wrap" gap={1.5} justifyContent="space-between" rowGap={0.75}>
        <Checkbox
          checked={rememberSignIn}
          disabled={isBusy}
          id="auth-identifier-remember"
          label={
            <Text component="span" variant="body2" visualVariant="inlineEmphasis">
              {SIGN_IN_REMEMBER_ME_LABEL}
            </Text>
          }
          onChange={onRememberSignInChange}
        />
        <TextLink<typeof RouterLink>
          component={RouterLink}
          disabled={isBusy}
          to={resetPasswordHref}
          variant="body2"
          visualVariant="anchor"
        >
          {SIGN_IN_FORGOT_PASSWORD_LABEL}
        </TextLink>
      </Box>
      <Button
        data-testid={SIGN_IN_TEST_IDS.identifierSubmitButton}
        disabled={isSubmitDisabled}
        fullWidth
        loading={isSubmitting}
        loadingBehavior="inline"
        type="submit"
        variant="contained"
        visualVariant="primary"
      >
        {SIGN_IN_IDENTIFIER_SUBMIT_LABEL}
      </Button>
      <AuthSocialButtons
        disabled={isBusy}
        onGoogleLogin={onStartGoogleLogin}
        onMicrosoftLogin={onMicrosoftLogin}
        socialProviderPending={socialProviderPending}
      />
    </Stack>
  );
};

export default SignInIdentifierStep;
